import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { SmePaymentAccountsApi } from '../tokens/sme-payment-accounts.token';
import { ActiveAccount, SmePaymentAccount } from '../models/sme-payment-account.model';
import { filenameFromResponse, saveBlob } from '../util/file-download';

/**
 * REST-only implementation of `SmePaymentAccountsApi` (EM-354, C1).
 *
 * Calls:
 *   GET  /services/embedded/api/parties/{partyId}/accounts
 *   POST /services/embedded/api/parties/{partyId}/active-account
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). Holds the shared
 * active-account signal the whole payments UX consumes (From-account default, list filter,
 * statement subtab, amount formatting). Rule 07 / Rule 10 — no Mock fallback; Playwright specs
 * intercept via `page.route()`.
 *
 * Error surfacing (Rule 09) is the caller's responsibility via `extractBackendErrorMessage`
 * — this service propagates the raw `HttpErrorResponse`.
 */
@Injectable()
export class SmePaymentAccountsRestService implements SmePaymentAccountsApi {
  private readonly http = inject(HttpClient);
  private readonly partiesBase = API_PATHS.PARTIES;

  private readonly accountsSig = signal<SmePaymentAccount[]>([]);
  private readonly activeAccountSig = signal<ActiveAccount | null>(null);

  readonly accounts = this.accountsSig.asReadonly();
  readonly activeAccount = this.activeAccountSig.asReadonly();
  readonly activeCurrency = computed<string | null>(() => this.activeAccountSig()?.currency ?? null);

  loadAccounts(partyId: number): Observable<SmePaymentAccount[]> {
    return this.http
      .get<SmePaymentAccount[]>(`${this.partiesBase}/${partyId}/accounts`)
      .pipe(tap((rows) => this.accountsSig.set(rows ?? [])));
  }

  setActiveAccount(partyId: number, accountId: number): Observable<ActiveAccount> {
    return this.http
      .post<ActiveAccount>(`${this.partiesBase}/${partyId}/active-account`, { accountId })
      .pipe(tap((active) => this.activeAccountSig.set(active)));
  }

  setActiveFromSession(activeAccountId: number | null, activeAccountCurrency: string | null): void {
    this.activeAccountSig.set({ accountId: activeAccountId, currency: activeAccountCurrency });
  }

  downloadStatement(
    partyId: number,
    accountId: number,
    from: string | null,
    to: string | null,
    format: 'csv' | 'pdf' | 'json',
  ): Observable<void> {
    let params = new HttpParams().set('format', format);
    if (from) {
      params = params.set('from', from);
    }
    if (to) {
      params = params.set('to', to);
    }
    const fallback = `statement.${format}`;
    return this.http
      .get(`${this.partiesBase}/${partyId}/accounts/${accountId}/statement`, {
        params,
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const blob = response.body ?? new Blob([], { type: format === 'json' ? 'application/json' : `text/${format}` });
          saveBlob(blob, filenameFromResponse(response, fallback));
        }),
      );
  }
}
